import {
  PublicCostLineDto,
  PublicCostLineKind,
  PublicSummaryParticipantDto,
} from '@/features/public/bookingSummary/types/BookingPublicSummaryDto.ts';

/**
 * Rachunek klienta w kształcie, w jakim rysuje go kafel: sekcje w kolejności, w której czyta się
 * rezerwację, i wiersz, który wie tylko tyle, ile ma pokazać.
 *
 * Grupowanie jest tu, a nie w kontrakcie, bo to decyzja tej strony: backend mówi, czym jest wiersz
 * (`kind`), a nie pod jakim nagłówkiem ma stanąć.
 */
export interface BillSectionVm {
  id: PublicCostLineKind;
  title: string;
  lines: PublicCostLineDto[];
}

/**
 * Nagłówki sekcji. Rabaty i pozycje ręczne stoją razem pod jednym napisem: dla klienta to jedno
 * i to samo — coś, co zmienia kwotę poza samą ofertą.
 */
const SECTION_TITLES: Record<PublicCostLineKind, string> = {
  [PublicCostLineKind.BASE]: 'Pakiet',
  [PublicCostLineKind.OPTION]: 'Aktywności dodatkowe',
  [PublicCostLineKind.TRANSPORT]: 'Transport',
  [PublicCostLineKind.DISCOUNT]: 'Zniżki i korekty',
  [PublicCostLineKind.MANUAL]: 'Zniżki i korekty',
};

/** Kolejność sekcji — ta sama, w której rachunek czyta organizator. */
const SECTION_ORDER: PublicCostLineKind[] = [
  PublicCostLineKind.BASE,
  PublicCostLineKind.OPTION,
  PublicCostLineKind.TRANSPORT,
  PublicCostLineKind.DISCOUNT,
];

/** Sekcja, do której trafia wiersz — pozycja ręczna dołącza do zniżek. */
const sectionOf = (kind: PublicCostLineKind): PublicCostLineKind =>
  kind === PublicCostLineKind.MANUAL ? PublicCostLineKind.DISCOUNT : kind;

export const bookingBillMapper = {
  /** Sekcje z pozycjami; sekcja bez wierszy nie powstaje, żeby kafel nie miał pustych nagłówków. */
  toSections(lines: PublicCostLineDto[]): BillSectionVm[] {
    return SECTION_ORDER.map((id) => ({
      id,
      title: SECTION_TITLES[id],
      lines: lines.filter((line) => sectionOf(line.kind) === id),
    })).filter((section) => section.lines.length > 0);
  },

  /**
   * Wiek uczestnika na dziś — kafel organizatora pokazuje go obok imienia, bo po nim rozwiązuje się
   * próg cenowy. Liczony z daty urodzenia, bo kontrakt wieku nie niesie i nie musi: to ta sama
   * arytmetyka co wszędzie, a data i tak jedzie.
   */
  ageOf(
    participant: PublicSummaryParticipantDto,
    today: Date = new Date(),
  ): number {
    const birth = new Date(participant.birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const beforeBirthday =
      today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() &&
        today.getDate() < birth.getDate());
    return beforeBirthday ? age - 1 : age;
  },
};
